import { MailPlus } from "lucide-react"
import { useTranslation } from "react-i18next"
import { CreateParams, useNewProps } from "./page-props"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Controller, SubmitHandler, useForm, useWatch } from "react-hook-form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { FormField } from "@/components/ui/form-field"
import { ErrorMessage } from "@hookform/error-message"
import { ErrorHint } from "@/components/ui/errorhint"
import { SheetPageBody, SheetPageTitle } from "@/components/ui/sheet-page"
import { useNavigate } from "@/lib/page-rendering"

type FormValues = {
  team_application: {
    team_profile_id: number
    state: "consideration" | "ready_to_accept"
  }
  ready_to_accept_message: string
  consideration_message: string
}

export default function Page() {
  const navigate = useNavigate()
  const { t } = useTranslation(["common", "team_applications"])

  const {
    team_application: { team_profile_id, team_profile_name },
  } = useNewProps()

  const { control, handleSubmit, register, formState } = useForm<FormValues>({
    defaultValues: {
      team_application: { team_profile_id },
      ready_to_accept_message: t(
        "team_applications:new.ready_to_accept_pro_forma",
      ),
      consideration_message: t("team_applications:new.consideration_pro_forma"),
    },
  })

  const watchState = useWatch({ control, name: "team_application.state" })

  const submit: SubmitHandler<FormValues> = (data) => {
    const { team_application, consideration_message, ready_to_accept_message } =
      data

    let message: string | null = null
    switch (team_application.state) {
      case "consideration":
        message = consideration_message
        break
      case "ready_to_accept":
        message = ready_to_accept_message
        break
    }

    navigate({
      href: ".",
      method: "post",
      data: { team_application, message } satisfies CreateParams,
    })
  }

  return (
    <SheetPageBody>
      <SheetPageTitle icon={MailPlus}>
        {t("team_applications:team_application.title", { team_profile_name })}
      </SheetPageTitle>
      <p className="mb-6">{t("team_applications:new.description")}</p>
      <form
        className="flex flex-col gap-6"
        data-testid="team-application-form"
        onSubmit={handleSubmit(submit)}
      >
        <input
          type="hidden"
          {...register("team_application.team_profile_id")}
        />

        <div className="space-y-2">
          <Label id="status-label" htmlFor="status-input">
            {t("team_applications:new.initial_status_label")}
          </Label>
          <Controller
            control={control}
            name="team_application.state"
            rules={{ required: t("common:errors.required") }}
            render={({ field: { onChange } }) => (
              <RadioGroup
                required
                aria-labelledby="status-label"
                id="status-input"
                onValueChange={(value) => onChange(value)}
                className="items-top flex w-full flex-col space-y-2 space-x-2"
              >
                <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
                  <div className="flex flex-row items-center">
                    <RadioGroupItem
                      value="consideration"
                      id="pending-review"
                      className="peer"
                      aria-labelledby="pending-review-label"
                      aria-describedby="pending-review-description"
                    />
                    <Label
                      htmlFor="pending-review"
                      className="flex items-center gap-2 rounded-md px-4 py-2"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium" id="pending-review-label">
                          {t(
                            "team_applications:new.initial_status.consideration",
                          )}
                        </span>
                        <span className="text-xs" id="private-description">
                          {t(
                            "team_applications:new.initial_status.consideration_description",
                          )}
                        </span>
                      </div>
                    </Label>
                  </div>
                  <FormField
                    {...register("consideration_message")}
                    label={t("team_applications:new.message_label")}
                  >
                    <Textarea disabled={watchState !== "consideration"} />
                  </FormField>
                </div>

                <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
                  <div className="flex flex-row items-center">
                    <RadioGroupItem
                      value="ready_to_accept"
                      id="ready-to-accept"
                      className="peer"
                      aria-labelledby="ready-to-accept-label"
                      aria-describedby="ready-to-accept-description"
                    />
                    <Label
                      htmlFor="ready-to-accept"
                      className="flex items-center gap-2 rounded-md px-4 py-2"
                    >
                      <div className="flex flex-col">
                        <span
                          className="font-medium"
                          id="ready-to-accept-label"
                        >
                          {t(
                            "team_applications:new.initial_status.ready_to_accept",
                          )}
                        </span>
                        <span className="text-xs" id="private-description">
                          {t(
                            "team_applications:new.initial_status.ready_to_accept_description",
                          )}
                        </span>
                      </div>
                    </Label>
                  </div>
                  <FormField
                    {...register("ready_to_accept_message")}
                    label={t("team_applications:new.message_label")}
                  >
                    <Textarea disabled={watchState !== "ready_to_accept"} />
                  </FormField>
                </div>
              </RadioGroup>
            )}
          />
          <ErrorMessage
            name="team_application.state"
            errors={formState.errors}
            render={ErrorHint}
          />
        </div>

        <Button type="submit">Send </Button>
      </form>
    </SheetPageBody>
  )
}
